import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AccountMenu from './AccountMenu';
import HeaderNav from './HeaderNav';
import HostEntry from './HostEntry';
import { CallNavPill } from '../calls/CallMiniBar';
import PostingRing from '../stories/PostingRing';
import { usePortalPosting } from '../../context/PortalPostingContext';
import NotificationDropdown from '../NotificationDropdown';
import PortalLogo from '../PortalLogo';
import PortalMark from '../PortalMark';
import { SearchGlyph, CloseGlyph, PlusGlyph, MessageGlyph, BellGlyph, ChevronGlyph } from './headerIcons';
import { useNotifications } from '../../context/NotificationContext';
import { useMessages } from '../../context/MessageContext';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import friendService from '../../services/friendService';
import useMediaQuery from '../../utils/useMediaQuery';
import SearchPanel from '../search/SearchPanel';
import { LocationPill, LocationPanel } from './LocationControl';
import { closeLocationPanel, isLocationPanelOpen, onLocationPanel, toggleLocationPanel } from '../../services/userLocation';
import useUserLocation from '../../hooks/useUserLocation';
import AddAccountModal from '../AddAccountModal';
import { getActivePage, setActivePage, isActivePage, onActivePageChange } from '../../services/activePage';
import proService from '../../services/proService';
import ProPagePicker, { actAs } from './ProPagePicker';
import { useChromeHeader } from './ChromeContext';
import './topHeader.css';

/**
 * The top bar — top-header handoff (Sept 25 2026, ~/Downloads/design_handoff_top_header, boards
 * 2a desktop / 2b phone), on every page. Black, and everything in it is round.
 *
 *   < 640   64px row, 16px gutter, 36px controls. Logo · search · messages · bell · avatar.
 *   640–1023 72px row, 28px gutter, 40px controls. Same right group.
 *   ≥ 1024  72px, a three-column grid so the nav pill track sits exactly centred. Right: search ·
 *           create · HOST · [wallet ≥1320] · messages · bell · avatar · chevron (name ≥1600).
 *
 * Below lg the floating bottom nav (MobileTabBar) carries the sections and HOST, which is where
 * all creating starts. Search is an icon at every width: it drops SearchPanel from the bar's
 * bottom edge (also on `/`, Esc closes). The wall's FILTERS tab hangs from the same edge
 * (wall/FilterDrawer) and steps aside while search is open.
 *
 * The logo keeps the brand floor (CLAUDE.md: cap ≥ 24, else PortalMark) rather than the
 * handoff's cap 20 on phones: the wordmark at 24 fits from 372 wide, and below that the portal
 * alone stands in at 32px.
 */
export const TOPBAR_HEIGHT = { phone: 64, desktop: 72 };
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const firstName = (name = '') => (name.trim().split(/\s+/)[0] || '').toUpperCase();

function Badge({ count, testId }) {
  if (!count) return null;
  return <span className="th-badge" data-testid={testId}>{count > 99 ? '99+' : count}</span>;
}

export default function TopBar(props) {
  const hosted = useChromeHeader('top', props);
  return hosted ? null : <TopBarContent {...props} />;
}

export function TopBarContent({
  user,
  walletBalance,
  onLogout,
  onLogoClick,
  onSearchOpen,
  className = '',
  /** 'admin' — the admin shell. Kept for its callers; it no longer changes anything. */
  variant, // eslint-disable-line no-unused-vars
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Your own Portal going up shows wherever your face does — the rail cell, here, and your
  // profile photo. Until it is live the circle is just your face.
  const { posting } = usePortalPosting();
  const isPosting = !!posting && posting.status !== 'up';
  const phone = useMediaQuery('(max-width: 639px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const roomyNav = useMediaQuery('(min-width: 1280px)');
  const walletWide = useMediaQuery('(min-width: 1320px)');
  const nameWide = useMediaQuery('(min-width: 1600px)');
  const wordmarkFits = useMediaQuery('(min-width: 372px)');
  // The location pill sits beside the logo from 640, except on a desktop under 1100 where the
  // centred nav needs the room — there (and on phones) it rides in the FILTERS tab instead.
  const pillRoom = useMediaQuery('(min-width: 1100px)');
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(isLocationPanelOpen);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  // The persona is only real if THIS account actually manages the page. Clearing
  // it on switch (accountRegistry.activateCanonical) stops new cases; this also
  // heals the stale values already sitting in browsers, and covers a page whose
  // access was revoked while you were wearing it.
  const [activePage, setActivePageState] = useState(getActivePage);
  // Every page the account can act as — drives SWITCH PAGE on the strip.
  const [pages, setPages] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Follow a switch made elsewhere in this tab (the dashboard's page switcher).
  useEffect(() => onActivePageChange((p) => { setActivePageState(p); setPickerOpen(false); }), []);
  useEffect(() => {
    const page = getActivePage();
    if (!page) return undefined;
    let done = false;
    proService.listMine()
      .then((r) => {
        if (done || !r?.success) return; // a failed list is not evidence — keep the persona
        const list = r.data || [];
        setPages(list);
        const row = list.find((p) => isActivePage(p, page));
        if (!row) { setActivePage(null); setActivePageState(null); return; }
        // Keep the strip's name and mark current with the page as it is now.
        if (row.name !== page.name || (row.kind && row.kind !== page.kind) || (row.role && row.role !== page.role)) {
          setActivePage({ ...page, id: row.id || page.id, name: row.name, avatarUrl: row.logoUrl || page.avatarUrl, kind: row.kind, role: row.role });
        }
      })
      .catch(() => { /* offline: leave it alone rather than dropping a valid persona */ });
    return () => { done = true; };
  }, []);
  const menuRef = useRef(null);
  const pickerRef = useRef(null);
  const searchBtnRef = useRef(null);
  const searchInputRef = useRef(null);
  // The mobile menu is portalled to <body>, so it is NOT inside menuRef — the
  // outside-click handler needs its own handle on the panel or a mousedown on a
  // row closes the menu before the click can navigate.
  const menuPanelRef = useRef(null);
  const notifRef = useRef(null);

  // Providers are absent on unauthenticated routes; mirror the old Navbar's
  // guarded reads rather than crashing there.
  let unreadCount = 0;
  try {
    const notif = useNotifications();
    unreadCount = notif.unreadCount;
  } catch { /* no NotificationProvider */ }

  let unreadMessageCount = 0;
  try {
    const msg = useMessages();
    unreadMessageCount = msg.unreadCount;
  } catch { /* no MessageProvider */ }

  let escrowBalance = 0;
  let userRole = null;
  let homeCity = '';
  try {
    const ctx = useUser();
    escrowBalance = ctx.escrowBalance || 0;
    userRole = ctx.role || null;
    homeCity = ctx.profile?.city || '';
  } catch { /* no provider */ }

  let portalRole = null;
  let isPortalMember = false;
  try {
    const auth = useAuth();
    portalRole = auth?.user?.portalRole || null;
    isPortalMember = !!auth?.user?.isPortalMember;
  } catch { /* no provider */ }

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await friendService.getPendingRequests();
        if (r?.success) setFriendRequestCount((r.data || []).length);
      } catch { /* non-critical */ }
    })();
  }, [user]);

  useEffect(() => {
    function onDown(e) {
      const insideMenu = menuRef.current?.contains(e.target) || menuPanelRef.current?.contains(e.target);
      if (!insideMenu) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    }
    if (menuOpen || notifOpen || pickerOpen) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen, notifOpen, pickerOpen]);

  // The city the whole app is showing events near. The pill prints it; the panel changes it;
  // the FILTERS tab's copy of the pill opens the same panel through `services/userLocation`.
  const browse = useUserLocation({ fallbackCity: homeCity });
  useEffect(() => onLocationPanel(setLocOpen), []);
  useEffect(() => { if (locOpen) { setSearchOpen(false); setMenuOpen(false); setNotifOpen(false); } }, [locOpen]);
  // Leaving the page closes it, the same way search closes.
  useEffect(() => () => closeLocationPanel(), []);

  const goHome = () => { if (user) navigate('/square'); else onLogoClick?.(); };
  const openSearch = () => { closeLocationPanel(); setSearchOpen(true); setMenuOpen(false); setNotifOpen(false); onSearchOpen?.(); };
  const closeSearch = () => setSearchOpen(false);

  // `/` opens search from anywhere — unless the user is typing in a field. Esc closes it.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape' && searchOpen) { setSearchOpen(false); return; }
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return;
      e.preventDefault();
      setSearchOpen(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  // A route change closes the panels (a result was opened, or the back button).
  useEffect(() => { setSearchOpen(false); closeLocationPanel(); }, [location.pathname]);

  // Lift the chrome over the page and fold the wall's FILTERS tab away while search is open
  // (topHeader.css keys both off this attribute — the tab lives in the page, not in here).
  useEffect(() => {
    const root = document.documentElement;
    if (searchOpen) root.setAttribute('data-pr-search-open', '');
    else root.removeAttribute('data-pr-search-open');
    return () => root.removeAttribute('data-pr-search-open');
  }, [searchOpen]);

  const rowH = phone ? 64 : 72;
  const sq = phone ? 36 : 40;
  const ico = phone ? 18 : 20;
  const onWallet = location.pathname === '/wallet';
  const showWallet = isDesktop && walletWide;

  const account = user ? (
    <AccountMenu
      user={user}
      walletBalance={walletBalance}
      friendRequestCount={friendRequestCount}
      unreadCount={unreadCount}
      unreadMessageCount={unreadMessageCount}
      userRole={userRole}
      portalRole={portalRole}
      isPortalMember={isPortalMember}
      onClose={() => setMenuOpen(false)}
      onLogout={onLogout}
      onAddAccount={() => setAddAccountOpen(true)}
      variant={isDesktop ? 'desktop' : 'mobile'}
      panelRef={menuPanelRef}
      barHeight={rowH}
    />
  ) : null;

  const avatarFace = isPosting ? (
    <PostingRing
      status={posting.status}
      progress={posting.progress}
      size={sq}
      avatarUrl={user?.avatarUrl}
      initial={(user?.displayName || 'Y')[0]}
      showPercent={false}
    />
  ) : (
    <span
      className="flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-mono text-[13px] font-bold text-black"
      style={{ width: sq, height: sq, ...(user?.avatarUrl ? {} : { background: 'linear-gradient(135deg,#FFCCE6,#A8D8EA)' }) }}
    >
      {user?.avatarUrl
        ? <img src={user.avatarUrl} alt="" className="block h-full w-full object-cover" />
        : (user?.displayName || '?').charAt(0).toUpperCase()}
    </span>
  );

  const unreadLabel = unreadCount ? `Notifications, ${unreadCount > 99 ? '99+' : unreadCount} unread` : 'Notifications';

  return (
    <header
      data-topbar
      data-search-open={searchOpen}
      className={`sticky top-0 w-full bg-black pt-safe-top font-mono text-white ${searchOpen || locOpen ? 'z-50' : 'z-40'} ${className}`}
      style={{ '--th-sq': `${sq}px` }}
    >
      <div
        className="relative grid items-center bg-black"
        style={{
          zIndex: 3,
          gridTemplateColumns: isDesktop ? 'minmax(max-content,1fr) auto minmax(max-content,1fr)' : 'minmax(0,1fr) auto',
          columnGap: phone ? 12 : 24,
          height: rowH,
          padding: `0 ${phone ? 16 : 28}px`,
        }}
      >
        <div className="flex min-w-0 items-center justify-self-start" style={{ gap: isDesktop && !roomyNav ? 10 : 14 }}>
          <button type="button" onClick={goHome} aria-label="Portal home" className="flex flex-shrink-0 items-center text-white transition-opacity hover:opacity-80">
            {/* The primary lockup, white on black: the spinning portal IS the O. */}
            {phone && !wordmarkFits ? <PortalMark size={32} /> : <PortalLogo cap={phone ? 24 : 26} />}
          </button>
          {user && !phone && (!isDesktop || pillRoom) && (
            <LocationPill
              city={browse.city}
              open={locOpen}
              onToggle={() => toggleLocationPanel()}
              size={sq}
              compact={isDesktop && !roomyNav}
            />
          )}
        </div>

        {isDesktop && (user ? <HeaderNav pad={roomyNav ? 16 : 12} /> : <span aria-hidden />)}

        {/* self-stretch so the cluster is the row's full height: the account and notification
            panels hang off `top-full`, and off a content-height cluster that lands 16px INSIDE
            the bar, tucking their top edge under it. */}
        <div className="flex items-center justify-self-end self-stretch" style={{ gap: phone ? 8 : 10 }}>
          <button
            ref={searchBtnRef}
            type="button"
            onClick={() => (searchOpen ? closeSearch() : openSearch())}
            aria-label={searchOpen ? 'Close search' : 'Search events, people, clubs'}
            aria-expanded={searchOpen}
            title="Search  /"
            data-on={searchOpen ? 'true' : 'false'}
            data-testid="search-button"
            className="th-icon"
          >
            {searchOpen ? <CloseGlyph size={16} /> : <SearchGlyph size={18} />}
          </button>

          {user ? (
            <>
              {/* A minimized call: ● ON A CALL · 2:14, back to the full call on click. */}
              <CallNavPill />
              {isDesktop && (
                <button type="button" onClick={() => navigate('/start')} aria-label="Create event" title="Create" data-portal-to="/start" className="th-create">
                  <PlusGlyph size={16} />
                </button>
              )}
              {isDesktop && <HostEntry label={nameWide} />}

              {showWallet && (
                <button type="button" onClick={() => navigate('/wallet')} aria-current={onWallet ? 'page' : undefined} className="th-wallet font-mono">
                  <span className="text-[14px] font-bold">{money(walletBalance)}</span>
                  <span className="whitespace-nowrap text-[9px] tracking-[0.08em]" style={{ color: '#9CA3AF' }}>+{money(escrowBalance)} ESCROW</span>
                </button>
              )}

              <button type="button" onClick={() => navigate('/messages')} aria-label="Messages" className="th-icon">
                <MessageGlyph size={ico} />
                <Badge count={unreadMessageCount} testId="messages-badge" />
              </button>

              {isDesktop ? (
                // The wrapper takes the row's full height so the dropdown's `top-full` lands on
                // the bar's bottom edge.
                <div className="relative flex items-center self-stretch" ref={notifRef}>
                  <button
                    type="button"
                    onClick={() => { setNotifOpen((v) => !v); setMenuOpen(false); }}
                    aria-label={unreadLabel}
                    aria-expanded={notifOpen}
                    data-on={notifOpen ? 'true' : 'false'}
                    className="th-icon"
                  >
                    <BellGlyph size={ico} />
                    <Badge count={unreadCount} testId="bell-badge" />
                  </button>
                  {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} />}
                </div>
              ) : (
                <button type="button" onClick={() => navigate('/notifications')} aria-label={unreadLabel} className="th-icon">
                  <BellGlyph size={ico} />
                  <Badge count={unreadCount} testId="bell-badge" />
                </button>
              )}

              <div className="relative flex items-center self-stretch" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => { setMenuOpen((v) => !v); setNotifOpen(false); }}
                  aria-label="Account"
                  aria-expanded={menuOpen}
                  className="flex flex-shrink-0 items-center rounded-full text-white transition-opacity hover:opacity-80"
                  style={{ gap: 8, height: sq }}
                >
                  {avatarFace}
                  {nameWide ? (
                    <span className="max-w-[160px] truncate text-[12px] font-semibold tracking-[0.14em]">{firstName(user.displayName)}</span>
                  ) : (
                    // Keep the name findable for assistive tech (and the account menu's tests)
                    // where there's no room to print it.
                    <span className="sr-only">{user.displayName}</span>
                  )}
                  {isDesktop && <ChevronGlyph size={12} />}
                </button>
                {menuOpen && account}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex flex-shrink-0 items-center rounded-full bg-white font-mono text-[11px] font-bold tracking-[0.14em] text-black transition-colors duration-200 hover:bg-[#FF00AA] hover:text-white"
              style={{ height: sq, padding: '0 16px' }}
            >
              SIGN IN
            </button>
          )}
        </div>
      </div>

      {/* Acting-as-Page strip — you're using Portal as your business page */}
      {activePage && (
        <div className="relative bg-black text-white" style={{ zIndex: 3, borderTop: '1px solid #262626' }} data-testid="acting-as-strip">
          <div className="mx-auto flex max-w-content items-center justify-between gap-3 px-4 py-1.5 sm:px-8">
            <span className="min-w-0 truncate font-mono text-[10px] font-bold uppercase tracking-widest">
              <span style={{ color: '#FF00FF' }}>Acting as</span> {activePage.name}
            </span>
            <div className="flex flex-shrink-0 items-center gap-2" ref={pickerRef}>
              {pages.length >= 2 && (
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  aria-expanded={pickerOpen}
                  data-testid="switch-page"
                  className={`rounded-full border-2 border-white px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest transition-colors hover:bg-white hover:text-black ${pickerOpen ? 'bg-white text-black' : ''}`}
                >
                  Switch page
                </button>
              )}
              <button
                type="button"
                onClick={() => { setActivePage(null); window.location.assign('/'); }}
                className="rounded-full border-2 border-white px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest transition-colors hover:bg-white hover:text-black"
              >
                Switch back
              </button>
              {pickerOpen && (
                <div className="absolute right-4 top-full z-50 mt-1 w-72 max-w-[calc(100vw-24px)] border-2 border-black bg-white text-black shadow-hard sm:right-8">
                  <div className="border-b-2 border-black px-5 pt-3 pb-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-gray-400">Act as</div>
                  <ProPagePicker pages={pages} active={activePage} onPick={actAs} onNew={() => { setPickerOpen(false); navigate('/pro/setup?new=1'); }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SearchPanel open={searchOpen} onClose={closeSearch} phone={phone} desktop={isDesktop} inputRef={searchInputRef} />

      {/* The location panel hangs off the same edge as search, over the same scrim. */}
      {locOpen && (
        <>
          <div className="th-scrim" onClick={closeLocationPanel} aria-hidden="true" style={{ opacity: 1 }} />
          <LocationPanel phone={phone} fallbackCity={homeCity} />
        </>
      )}

      {addAccountOpen && <AddAccountModal onClose={() => setAddAccountOpen(false)} />}
    </header>
  );
}
